import { compareFeaturesByCategory } from '@components/OrgFeatureToggleList';

import type { OrgFeatureModel } from 'generated/types';

import type { PatientPlanValue } from './types';

/** Description limit from the ticket; enforced by the input and by Yup. */
export const DESCRIPTION_CHAR_LIMIT = 250;

let planCounter = 0;

/**
 * Identity for a plan that does not exist server-side yet. A counter rather
 * than a random id, so card keys stay stable and tests deterministic; never
 * sent to the API.
 */
export function nextPlanLocalId(): string {
  planCounter += 1;
  return `plan-${planCounter}`;
}

export function makeEmptyPlan(name = ''): PatientPlanValue {
  return {
    local_id: nextPlanLocalId(),
    name,
    description: '',
    seats: 0,
    feature_ids: [],
  };
}

/**
 * Patient features a plan may include: enabled, offered in plans, and carrying
 * a patient-facing name. Same filter whether the ids are about to be enabled
 * (wizard) or already are (org settings).
 *
 * Returned in the order the License & features page lists the org toggles, so
 * an admin reads the same features in the same sequence on both screens. That
 * is why the sort is the toggle list's own comparator rather than
 * `display_order` alone, which restarts per category and interleaves them.
 */
export function getEligibleFeatures(
  features: OrgFeatureModel[],
  orgFeatureIds: string[]
): OrgFeatureModel[] {
  return features
    .filter(
      (feature) =>
        orgFeatureIds.includes(feature.feature_id) &&
        feature.is_available_for_plan &&
        Boolean(feature.patient_display_name)
    )
    .sort(compareFeaturesByCategory);
}

/** Seats handed out across every plan, tolerating cleared numeric inputs. */
export function sumAllocatedSeats(plans: PatientPlanValue[]): number {
  return plans.reduce((sum, plan) => sum + (Number(plan.seats) || 0), 0);
}
